<template>
  <div class="setup-portal">
    <header class="setup-header">
      <div class="setup-header-title">
        <Title size="4_5">
          <p>Client Portal Setup</p>
        </Title>
      </div>

      <nav class="setup-header-nav">
        <Pills class="setup-header-pills">
          <PillsList class="setup-header-pills-list">
            <PillItem
              v-for="pill in pills"
              :key="pill.label"
              class="setup-nav-item"
              :icon="pill.icon"
              :to="pill.to"
            >
              {{ pill.label }}
            </PillItem>
          </PillsList>
        </Pills>
      </nav>

      <aside class="setup-header-status">
        <p
          v-if="context.client"
          class="setup-header-name"
        >
          For
          <router-link
            :to="{ name: 'entity-detail', params: { entityExternalId: context.client.external_id } }"
            target="_blank"
            class="tw-inline-flex tw-items-center"
          >
            {{ context.client.name }}
            <Icon
              icon="fas fa-external-link-alt"
              size="3"
            />
          </router-link>
        </p>
      </aside>
    </header>

    <div v-if="$route.name === 'advisor-client-portal-setup-preview'">
      <router-view :context="context" />
    </div>

    <main
      v-else
      class="setup-main"
    >
      <div class="setup-main-body">
        <router-view :context="context" />
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Icon, Pills, PillsList, PillItem, Title } from '@stakeui';

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  firmSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: false,
    default: '',
  },
  advisorView: {
    type: Boolean,
    default: false,
  },
});

const context = ref({});
const isFetching = ref(false);
const hasFetched = ref(false);

const pills = computed(() => [
  {
    label: 'Checklist setup',
    icon: 'fas fa-tasks',
    to: { name: 'advisor-client-portal-setup-checklist' },
  },
  {
    label: 'Preview Portal',
    icon: 'fas fa-eye',
    to: { name: 'advisor-client-portal-setup-preview' },
  },
  {
    label: 'Send link to Client',
    icon: 'fas fa-link',
    to: { name: 'advisor-client-portal-setup-link' },
  },
]);

const fetchPortalContext = async (firmSlug, advisorSlug, entityExternalId) => {
  isFetching.value = true;

  const url = `/api/v1/client-portal/context/${firmSlug}/${advisorSlug}/${entityExternalId}`;

  try {
    const response = await fetch(
        url, { credentials: 'include' },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch context: ${response.status}`);
    }

    const data = await response.json();
    context.value = data;
  } catch (err) {
    console.error(`Error fetching context: ${err.message}`);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

onMounted(async () => {
  await fetchPortalContext(
      props.firmSlug,
      props.advisorSlug,
      props.entityExternalId,
  );
});
</script>

<style scoped>
.setup-portal {
  --spacing: 1.5em;
  --box-inner-shadow: 0 -1px 0 0 rgba(0, 0, 0, 0.10) inset, 0 1px 0 0 rgba(255, 255, 255, 0.10) inset;
  --page-background: #F3F4F6;
  background-color: var(--page-background);
  min-height: 100vh;
}

.setup-header {
  background-color: #fff;
  padding: var(--spacing);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--spacing);
  text-align: center;
  box-shadow: var(--box-inner-shadow);
}

.setup-header-name {
  color: var(--stk-text-normal);
}

.setup-header-nav {
  width: 20em;
}

.setup-header-pills {
  padding: 0.25em;
  border-radius: 0.625em;
}

.setup-header-pills-list {
  width: 100%;
}

.setup-nav-item {
  width: 100%;
}

.setup-header-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
}

.setup-main {
  display: flex;
  justify-content: center;
  padding: var(--spacing);
  padding-bottom: calc(2 * var(--spacing));
}

.setup-main-body {
  flex-grow: 1;
  max-width: 45em;
  width: 100%;
}

@media screen and (min-width: 920px) {
  .setup-header {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    justify-content: space-between;
  }

  .setup-header-nav,
  .setup-nav-item {
    width: auto;
  }
}

@media screen and (min-width: 1140px) {
  .setup-header {
    grid-template-columns: 1fr 45em 1fr;
  }

  .setup-header-nav {
    width: 45em;
  }

  .setup-nav-item {
    width: 33.3333%;
  }
}
</style>

<style>
.floating-chat-button,
.footer {
  display: none;
}
</style>
